import './RoleActionMenu.scss';

import * as React from 'react';

import { SlSkeleton } from '@shoelace-style/shoelace/dist/react';
import { TeamRole } from '@showbie-socrative/socrative-utils';

import { assertUnreachable } from '../../../../../../util/assertUnreachable';
import { useRoleOnSelectedTeam } from './hooks';
import { RoleActionMenuAdmin } from './RoleActionMenuAdmin';
import { RoleActionMenuOwner } from './RoleActionMenuOwner';
import { StaticRoleName } from './StaticRoleName';

type Permutations<E1 extends string, E2 extends string> = `${E1}-${E2}`;
type AllPermutations = Permutations<
  keyof typeof TeamRole,
  keyof typeof TeamRole
>;

interface RoleActionMenuProps {
  targetMemberRole: TeamRole;
  targetPublicId: string;
}

export function RoleActionMenu({
  targetMemberRole,
  targetPublicId,
}: RoleActionMenuProps) {
  // Get the role of the acting user on the selected team
  const roleOnSelectedTeam = useRoleOnSelectedTeam();

  // If the role data is not yet available, show a loading skeleton
  if (!roleOnSelectedTeam.data) {
    return <SlSkeleton />;
  }

  return (
    <RoleCombination
      actingMemberRole={roleOnSelectedTeam.data}
      targetMemberRole={targetMemberRole}
      targetPublicId={targetPublicId}
    />
  );
}

interface RoleCombinationProps {
  actingMemberRole: TeamRole;
  targetMemberRole: TeamRole;
  targetPublicId: string;
}

function RoleCombination({
  targetMemberRole,
  actingMemberRole,
  targetPublicId,
}: RoleCombinationProps) {
  const roleCombo =
    `${TeamRole[actingMemberRole]}-${TeamRole[targetMemberRole]}` as AllPermutations;

  // Render the appropriate RoleActionMenu component based on the role combination
  switch (roleCombo) {
    case 'Admin-Member':
    case 'Admin-Admin':
      // If an Admin is interacting with a Member or an admin, render the RoleActionMenuAdmin component
      return (
        <RoleActionMenuAdmin
          targetPublicId={targetPublicId}
          targetMemberRole={targetMemberRole}
        />
      );
    case 'Owner-Admin':
    case 'Owner-Member':
      // If an Owner is interacting with an Admin or a Member, render the RoleActionMenuOwner component
      return (
        <RoleActionMenuOwner
          targetPublicId={targetPublicId}
          targetMemberRole={targetMemberRole}
        />
      );
    case 'Owner-Owner':
    case 'Admin-Owner':
    case 'Member-Owner':
    case 'Member-Admin':
    case 'Member-Member':
      // For all other combinations, render the StaticRoleName component
      return <StaticRoleName targetMemberRole={targetMemberRole} />;
    default:
      // If the role combination is unexpected, assert unreachable in development mode
      if (__DEV__) {
        assertUnreachable(roleCombo);
      }
      // Render the StaticRoleName component as a fallback
      return <StaticRoleName targetMemberRole={targetMemberRole} />;
  }
}
